import { pascalCase } from 'change-case'
import React, {
    type ChangeEvent,
    type ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useScreenshotMode } from '../../hooks/useScreenshotMode'
import type { EntityId, SelectionI } from '../../types/constants'
import { axiosGet, axiosPost } from '../../util/api'
import { number } from '../../util/formatters'
import { AdGroupName } from '../table/AdGroupName'
import { CampaignName } from '../table/CampaignName'
import {
    BulkActionDialog,
    BulkActionDialogActions,
    BulkActionDialogResultActions,
    BulkActionField,
    BulkActionSelectionSummary,
    bulkActionInputClassName,
} from './BulkActionDialog'
import { BulkActionStatus, type BulkActionStatusPhase } from './BulkActionStatus'
import type { BulkActionRunInfo } from './bulkActionExecution'

export const MAX_PRODUCT_ADS_PER_ACTION = 500_000

export type ProductIdentifier = 'asin' | 'sku'
export type ProductAdFlowStep = 'configure' | 'confirm' | 'submitting' | 'running' | 'result'

export interface ProductAdRunCallbacks {
    onSuccess: (result: BulkActionRunInfo) => void
    onFailure: (error: unknown) => void
}

export type ProductAdAsyncAcknowledgement = (
    uuid: string,
    progress: unknown,
    callbacks: ProductAdRunCallbacks,
) => ProductAdRunDismissal | undefined

export type ProductAdRunDismissal = () => void

export interface ProductAdBulkActionModalProps {
    identifier: ProductIdentifier
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
    selectedValues: SelectionI
    onAsyncAcknowledged?: ProductAdAsyncAcknowledgement
    initialInput?: string
    initialStep?: ProductAdFlowStep
    initialResult?: BulkActionRunInfo
}

export interface ProductAdInputSummary {
    entries: string[]
    validUnique: string[]
    invalid: string[]
    duplicateCount: number
    productAdCount: number
    overLimit: boolean
}

export const getProductAdInputSummary = (
    input: string,
    identifier: ProductIdentifier,
    adGroupCount: number,
): ProductAdInputSummary => {
    const entries = input.replace(/\n/g, ',').split(',').filter(Boolean)
    // Preserve the existing vendor validation contract: ASINs must contain 10 characters.
    const invalid = identifier === 'asin' ? entries.filter((entry) => entry.length !== 10) : []
    const validEntries = entries.filter((entry) => !invalid.includes(entry))
    const validUnique = Array.from(new Set(validEntries))
    const productAdCount = validUnique.length * adGroupCount

    return {
        entries,
        validUnique,
        invalid,
        duplicateCount: validEntries.length - validUnique.length,
        productAdCount,
        overLimit: productAdCount > MAX_PRODUCT_ADS_PER_ACTION,
    }
}

const getRunUuid = (progress: unknown): string | undefined => {
    if (!progress || typeof progress !== 'object') {
        return undefined
    }

    const entries = Object.values(progress as Record<string, any>)
    const productAds = (progress as Record<string, any>)[pascalCase('ProductAds')]
    return (
        productAds?.uuid ?? productAds?.extra?.uuid ?? entries[0]?.uuid ?? entries[0]?.extra?.uuid
    )
}

/**
 * `counts` absent is not the same as `counts` zero: the first means the service
 * told us nothing, the second means nothing was created. Both used to render as
 * an unqualified success.
 */
const resultPresentation = (result: BulkActionRunInfo) => {
    const succeeded = result.counts?.success
    const failed = result.counts?.failed
    const hasCounts = typeof succeeded === 'number' || typeof failed === 'number'

    if (!hasCounts) {
        return {
            phase: 'succeeded' as BulkActionStatusPhase,
            counts: undefined,
            title: 'Product-ad creation finished',
            message: 'Amazon did not report per-item counts. Check the ad groups table.',
        }
    }

    const created = succeeded ?? 0
    const rejected = failed ?? 0
    const phase: BulkActionStatusPhase =
        created > 0 ? (rejected > 0 ? 'partially_succeeded' : 'succeeded') : 'failed'

    return {
        phase,
        counts: { succeeded, failed },
        title:
            created > 0
                ? rejected > 0
                    ? 'Some product ads were created'
                    : 'Product ads created'
                : 'No product ads were created',
        // The title and the outcome line say everything; neither a refresh nor
        // its absence needs restating.
        message: undefined,
    }
}

export const ProductAdBulkActionModal = ({
    identifier,
    isOpen,
    setIsOpen,
    selectedValues,
    onAsyncAcknowledged,
    initialInput = '',
    initialStep = 'configure',
    initialResult,
}: ProductAdBulkActionModalProps) => {
    const [input, setInput] = useState(initialInput)
    const [step, setStep] = useState<ProductAdFlowStep>(initialStep)
    const [result, setResult] = useState<BulkActionRunInfo | undefined>(initialResult)
    const [failureMessage, setFailureMessage] = useState<string>()
    const dismissOwnedRun = useRef<ProductAdRunDismissal>()
    const adGroupIds = selectedValues.safeList
    const adGroupCount = selectedValues.selectedCount || adGroupIds.length
    const summary = useMemo(
        () => getProductAdInputSummary(input, identifier, adGroupCount),
        [adGroupCount, identifier, input],
    )
    const label = identifier === 'asin' ? 'ASIN' : 'SKU'
    const pluralLabel = `${label}s`

    useEffect(
        () => () => {
            dismissOwnedRun.current?.()
        },
        [],
    )

    const close = () => {
        dismissOwnedRun.current?.()
        dismissOwnedRun.current = undefined
        setIsOpen(false)
    }
    const backToConfigure = () => {
        setFailureMessage(undefined)
        setStep('configure')
    }

    const submit = () => {
        if (summary.validUnique.length === 0 || summary.overLimit) {
            return
        }

        setStep('submitting')
        setFailureMessage(undefined)
        const create = summary.validUnique.map((value) => ({ [identifier]: value }))
        const payload: Payload.Linked = {
            class: 'ProductAds',
            linked: { class: 'AdGroups', ids: adGroupIds },
            create,
        }

        axiosPost('bulk-actions/create', payload)
            .then((response) => {
                const uuid = getRunUuid(response.data?.progress)
                if (!uuid) {
                    setFailureMessage('The bulk action started without a progress ID.')
                    setStep('result')
                    return
                }

                setStep('running')
                dismissOwnedRun.current = onAsyncAcknowledged?.(uuid, response.data.progress, {
                    onSuccess: (terminalResult) => {
                        dismissOwnedRun.current = undefined
                        setResult(terminalResult)
                        setStep('result')
                    },
                    onFailure: (error: any) => {
                        dismissOwnedRun.current = undefined
                        setFailureMessage(
                            error?.response?.data?.message ??
                                error?.message ??
                                'The product ads could not be created.',
                        )
                        setStep('result')
                    },
                })
            })
            .catch((error: any) => {
                setFailureMessage(
                    error?.response?.data?.message ??
                        error?.message ??
                        'The product ads could not be created.',
                )
                setStep('result')
            })
    }

    const isConfigure = step === 'configure'
    const isConfirm = step === 'confirm'
    const isActive = step === 'submitting' || step === 'running'
    const displayResult = resultPresentation(result ?? {})
    const title = `Add ${pluralLabel} to ad groups`

    let footer: ReactNode
    if (isConfigure) {
        footer = (
            <BulkActionDialogActions
                primaryLabel="Review product ads"
                onPrimary={() => setStep('confirm')}
                onCancel={close}
                // Blocking the limit here too, so it is not discovered one step later.
                disabled={summary.validUnique.length === 0 || summary.overLimit}
            />
        )
    } else if (isConfirm) {
        footer = (
            <BulkActionDialogActions
                primaryLabel="Create product ads"
                onPrimary={submit}
                onCancel={backToConfigure}
                cancelLabel="Back"
                disabled={summary.overLimit || summary.validUnique.length === 0}
            />
        )
    } else if (step === 'submitting') {
        footer = undefined
    } else if (step === 'running') {
        footer = undefined
    } else if (failureMessage) {
        footer = (
            <BulkActionDialogActions
                primaryLabel="Try again"
                onPrimary={backToConfigure}
                onCancel={close}
                cancelLabel="Close"
            />
        )
    } else {
        footer = <BulkActionDialogResultActions onDismiss={close} />
    }

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={close}
            title={title}
            // Instructional copy belongs to the step that needs it, not to the
            // running and result screens where it is stale.
            description={
                isConfigure
                    ? `Create one product ad for each valid ${label} and selected ad group.`
                    : undefined
            }
            width="wide"
            dismissible={!isActive}
            footer={footer}
        >
            {isConfigure && (
                <>
                    <BulkActionSelectionSummary
                        selection={{ kind: 'selected', count: adGroupCount }}
                        entity="ad group"
                    />
                    <div className="grid gap-[20px] md:grid-cols-[minmax(220px,0.8fr)_minmax(360px,1.2fr)]">
                        <BulkActionField
                            label={pluralLabel}
                            htmlFor={`product-ad-${identifier}-input`}
                            description={`Separate ${pluralLabel} with commas or new lines.`}
                            // Discards do not block submission, so they must not
                            // take the error tone or displace the format hint.
                            warning={
                                summary.invalid.length > 0
                                    ? `${number(summary.invalid.length)} invalid ${pluralLabel} will be discarded.`
                                    : undefined
                            }
                            error={
                                summary.overLimit
                                    ? `This exceeds the ${number(MAX_PRODUCT_ADS_PER_ACTION)} product-ad limit.`
                                    : undefined
                            }
                            required
                        >
                            <textarea
                                id={`product-ad-${identifier}-input`}
                                className={`${bulkActionInputClassName} min-h-[220px] resize-y font-mono`}
                                value={input}
                                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                                    setInput(event.target.value)
                                }
                                placeholder={`Enter ${pluralLabel}, one per line`}
                            />
                            <div className="mt-[8px] flex flex-wrap gap-x-[14px] gap-y-[4px] text-[12px] text-[#5f6b7c]">
                                <span>{number(summary.entries.length)} entered</span>
                                <span>{number(summary.validUnique.length)} valid and unique</span>
                                {summary.duplicateCount > 0 && (
                                    <span>{number(summary.duplicateCount)} duplicate</span>
                                )}
                            </div>
                        </BulkActionField>
                        <section aria-label="Selected ad groups">
                            <h3 className="m-0 mb-[8px] text-[14px] font-semibold text-[#1c2127]">
                                Selected ad groups
                            </h3>
                            <div className="max-h-[300px] overflow-auto rounded-[6px] border border-[#d8e0e8]">
                                <SelectedAdGroupsPreview adGroupIds={adGroupIds} />
                            </div>
                        </section>
                    </div>
                </>
            )}
            {isConfirm && (
                <>
                    <BulkActionSelectionSummary
                        selection={{ kind: 'selected', count: adGroupCount }}
                        entity="ad group"
                    />
                    <div className="text-[14px] leading-[21px] text-[#3d4852]">
                        <p className="m-0">
                            Creating{' '}
                            <span className="font-semibold text-[#1c2127]">
                                {number(summary.productAdCount)} product ads
                            </span>{' '}
                            — {number(summary.validUnique.length)} unique {pluralLabel} across{' '}
                            {number(adGroupCount)} ad groups.
                        </p>
                        {(summary.invalid.length > 0 || summary.duplicateCount > 0) && (
                            <p className="m-0 mt-[6px] text-[#5f6b7c]">
                                Discarding{' '}
                                {[
                                    summary.invalid.length > 0 &&
                                        `${number(summary.invalid.length)} invalid`,
                                    summary.duplicateCount > 0 &&
                                        `${number(summary.duplicateCount)} duplicate`,
                                ]
                                    .filter(Boolean)
                                    .join(' and ')}{' '}
                                {pluralLabel}.
                            </p>
                        )}
                        {summary.overLimit && (
                            <p role="alert" className="m-0 mt-[8px] font-medium text-[#a82a2f]">
                                This exceeds the {number(MAX_PRODUCT_ADS_PER_ACTION)} product-ad
                                limit.
                            </p>
                        )}
                    </div>
                </>
            )}
            {isActive && (
                <BulkActionStatus
                    phase={step === 'submitting' ? 'queued' : 'running'}
                    title={
                        step === 'submitting'
                            ? 'Starting product-ad creation'
                            : 'Creating product ads'
                    }
                    message="Amazon is creating the product ads. The ad groups table will refresh when this finishes."
                />
            )}
            {step === 'result' && failureMessage && (
                <BulkActionStatus
                    phase="failed"
                    title="Product ads could not be created"
                    message={failureMessage}
                />
            )}
            {step === 'result' && !failureMessage && (
                <BulkActionStatus
                    phase={displayResult.phase}
                    verb="created"
                    entity="product ad"
                    entityPlural="product ads"
                    title={displayResult.title}
                    message={result?.message ?? displayResult.message}
                    counts={displayResult.counts}
                />
            )}
        </BulkActionDialog>
    )
}

interface SelectedAdGroup {
    ad_group_id: EntityId
    name: string
    campaign_id: EntityId
    campaign_name: string
    campaign_state?: string
    state?: string
}

interface AdGroupPreviewPage {
    items: SelectedAdGroup[]
    currentPage: number
    lastPage: number
    total: number | undefined
}

type AdGroupPreviewState =
    | { phase: 'loading'; page: AdGroupPreviewPage }
    | { phase: 'failed'; page: AdGroupPreviewPage }
    | { phase: 'loaded'; page: AdGroupPreviewPage }

const AD_GROUP_PREVIEW_PER_PAGE = 15

const emptyPreviewPage = (currentPage = 1): AdGroupPreviewPage => ({
    items: [],
    currentPage,
    lastPage: 1,
    total: undefined,
})

/**
 * This intentionally owns its request rather than borrowing the generic table.
 * The modal needs a stable, small preview of exactly the selected ad groups and
 * must not inherit table preferences, profile commits, or pagination UI.
 */
const SelectedAdGroupsPreview = ({ adGroupIds }: { adGroupIds: EntityId[] }) => {
    const screenshotMode = useScreenshotMode()
    const [retry, setRetry] = useState(0)
    const [page, setPage] = useState(1)
    const [preview, setPreview] = useState<AdGroupPreviewState>({
        phase: 'loading',
        page: emptyPreviewPage(),
    })
    const latestRequest = useRef(0)
    const adGroupSelectionKey = adGroupIds.map((id) => `${typeof id}:${id}`).join('|')

    useEffect(() => {
        setPage(1)
    }, [adGroupSelectionKey])

    useEffect(() => {
        const request = ++latestRequest.current
        setPreview({ phase: 'loading', page: emptyPreviewPage(page) })

        axiosGet(
            'api/v5/ad-groups',
            {
                page,
                perPage: AD_GROUP_PREVIEW_PER_PAGE,
                query: undefined,
                filter: {
                    // The v5 browse endpoint accepts the selected IDs as one
                    // bigint-safe IN filter. Keep the original strings intact.
                    ad_group_id: { in: adGroupIds.join(',') },
                },
            },
            true,
        )
            .then((response) => {
                if (request !== latestRequest.current) {
                    return
                }

                const data = response.data ?? {}
                const pagination = data.meta ?? data
                setPreview({
                    phase: 'loaded',
                    page: {
                        items: data.data ?? [],
                        currentPage: pagination.current_page ?? page,
                        lastPage: pagination.last_page ?? 1,
                        total: pagination.total,
                    },
                })
            })
            .catch(() => {
                if (request === latestRequest.current) {
                    setPreview({ phase: 'failed', page: emptyPreviewPage(page) })
                }
            })

        // Ignore pending responses after an ID change or unmount. The shared API
        // error wrapper is not cancellation-safe, so stale-response
        // protection is deliberately kept at this boundary.
        return () => {
            latestRequest.current++
        }
    }, [adGroupIds, page, retry])

    if (preview.phase === 'loading') {
        return <p className="m-0 p-[12px] text-[13px] text-[#5f6b7c]">Loading ad groups…</p>
    }

    if (preview.phase === 'failed') {
        return (
            <div className="p-[12px] text-[13px] text-[#5f6b7c]" role="alert">
                <p className="m-0">Selected ad groups could not be loaded.</p>
                <button
                    type="button"
                    className="mt-[8px] text-[#1769aa] underline"
                    onClick={() => {
                        setPage(1)
                        setRetry((current) => current + 1)
                    }}
                >
                    Retry
                </button>
            </div>
        )
    }

    const paginationControls =
        preview.page.lastPage > 1 ? (
            <div className="flex items-center justify-between border-t border-[#e6ebf0] px-[12px] py-[8px] text-[12px] text-[#5f6b7c]">
                <span>
                    Page {preview.page.currentPage} of {preview.page.lastPage}
                    {typeof preview.page.total === 'number' &&
                        ` (${number(preview.page.total)} total)`}
                </span>
                <div className="flex gap-[8px]">
                    <button
                        type="button"
                        className="text-[#1769aa] underline disabled:text-[#9ba8b7]"
                        disabled={preview.page.currentPage <= 1}
                        onClick={() => setPage(preview.page.currentPage - 1)}
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        className="text-[#1769aa] underline disabled:text-[#9ba8b7]"
                        disabled={preview.page.currentPage >= preview.page.lastPage}
                        onClick={() => setPage(preview.page.currentPage + 1)}
                    >
                        Next
                    </button>
                </div>
            </div>
        ) : null

    if (preview.page.items.length === 0) {
        return (
            <>
                <p className="m-0 p-[12px] text-[13px] text-[#5f6b7c]">No ad groups found.</p>
                {paginationControls}
            </>
        )
    }

    return (
        <>
            <ul className="m-0 list-none divide-y divide-[#e6ebf0] p-0">
                {preview.page.items.map((adGroup) => (
                    <li key={String(adGroup.ad_group_id)} className="p-[12px] text-[13px]">
                        <CampaignName
                            screenshotMode={screenshotMode}
                            showBadge={false}
                            campaign={{
                                name: adGroup.campaign_name,
                                state: adGroup.campaign_state,
                                campaign_id: adGroup.campaign_id,
                            }}
                        />
                        <AdGroupName
                            showState={false}
                            screenshotMode={screenshotMode}
                            adGroup={adGroup as any}
                        />
                    </li>
                ))}
            </ul>
            {paginationControls}
        </>
    )
}
